import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FlagIcon from "@mui/icons-material/Flag";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GroupIcon from "@mui/icons-material/Group";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Space3DURL } from "../../utility/initApp.js";
import LayersIcon from "@mui/icons-material/Layers";
import AssigneePopup from "../AssigneePopup";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import Service from "../../api/service";
import { showSuccessToast, showErrorToast } from "../toasts/ToastNotification";
import DeliverablesDots from "../DeliverablesDots";

import {
  openGlobalDropdown,
  closeGlobalDropdown,
  isGlobalDropdownOpen,
  subscribeToDropdownChanges
} from "../../utility/dropdownState";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  const first = parts[0]?.charAt(0).toUpperCase() || "";
  const last =
    parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : "";
  return first + last;
};



const calculateTooltipSize = (text, fontSize = 12, padding = 10) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = `${fontSize}px Arial`;
  const lines = text.split("\n");
  const maxWidth = Math.max(
    ...lines.map((line) => context.measureText(line).width)
  );
  const height = lines.length * (fontSize + 4);
  return { width: maxWidth + padding * 2, height: height + padding * 2 };
};

const RectangleNode = ({
  id,
  x,
  y,
  width = 160,
  height = 60,
  title = "",
  subtitle = "",
  startDate,
  isChild,
  dueDate,
  assignees: d = [],
  maturity = "", // Changed from status to maturity
  maturityDescription = "",
  status = "",
  statusDescription = "",
  containerRef,
  description = "",
  dragNode,
  hasChildren = false,
  isExpanded = false,
  onDoubleClick = null,
  onMouseDown,
  isInitial = false,
  hierarchyDepth = 0,
  objectId,
  totalFloat = 0,
  showCriticalPath = false,
  isInChain = false,
  onHighlightChain = null,
  opacity = 1,
  chainRootId = null,
  exitedChainNodes = new Set(),
  loading = false,
  forceResetHover = false,
  projectId,
  onAddDeliverables = null,
  onRefresh,
  onUpdateNode,
  onPromoteNode,
  nodeColor, // Standardized prop for background fill
  delayDays, // Calculated delay from NodeRenderer
  timelineMode,
  zoomRatio,
}) => {
  // Safe status check
  const safeStatus = (status || "").toLowerCase().trim();
  const showWarningIcon =
    safeStatus === "late" || safeStatus === "delay" || safeStatus === "delayed" || delayDays > 0;
  const nodeRef = useRef(null);
  const dropdownRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    textNode: null,
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });
  const ENOVIA_BASE_URL = Space3DURL;

  // Local state to track if this dropdown is open (using global state)
  const [isThisDropdownOpen, setIsThisDropdownOpen] = useState(false);
  const [showAssigneePopup, setShowAssigneePopup] = useState(false);

  // Effect to sync with global dropdown state
  useEffect(() => {
    const updateLocalState = () => {
      setIsThisDropdownOpen(isGlobalDropdownOpen(id));
    };

    // Initial sync
    updateLocalState();

    // Subscribe to global changes using utility
    return subscribeToDropdownChanges(updateLocalState);
  }, [id]);

  const showLocalTooltip = (tooltipString, tooltipNode) => {
    const { width: tw, height: th } = calculateTooltipSize(tooltipString);
    setTooltip({
      visible: true,
      text: tooltipString,
      textNode: tooltipNode,
      width: tw,
      height: th,
      x: width - tw - 8,
      y: height + 8,
    });
  };

  const hideLocalTooltip = () => {
    setTooltip((t) => ({ ...t, visible: false }));
  };

  const [highlighted, setHighlighted] = useState(false);

  // ==================== CRITICAL FIX: Hover Reset Logic ====================

  // Effect 1: Reset hover when chain status changes
  useEffect(() => {
    setHighlighted(false);
    hideLocalTooltip();
  }, [isInChain]);

  // Effect 2: Reset hover when forceResetHover prop changes
  useEffect(() => {
    if (forceResetHover) {
      setHighlighted(false);
      hideLocalTooltip();
    }
  }, [forceResetHover]);

  // Effect 3: Reset hover when menu opens
  useEffect(() => {
    if (isThisDropdownOpen) {
      setHighlighted(false);
      hideLocalTooltip();
    }
  }, [isThisDropdownOpen]);

  // Effect 4: Reset hover when component unmounts (moving between layers)
  useEffect(() => {
    return () => {
      setHighlighted(false);
      hideLocalTooltip();
    };
  }, []);

  // Effect 5: Global mousemove guard — hides tooltip if cursor leaves node boundary
  // Necessary because SVG onMouseLeave is unreliable over foreignObject children
  useEffect(() => {
    if (!tooltip.visible) return;
    const handleGlobalMove = (e) => {
      const rect = nodeRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        hideLocalTooltip();
        setHighlighted(false);
      }
    };
    document.addEventListener("mousemove", handleGlobalMove);
    return () => document.removeEventListener("mousemove", handleGlobalMove);
  }, [tooltip.visible]);

  const isCritical = showCriticalPath && totalFloat === 0;

  // ==================== Memoized Stroke Color ====================
  const rectStroke = useMemo(() => {
    // Hover / highlight should slightly brighten, not override meaning
    if (highlighted && isInChain) return "#3B82F6"; // brighter blue
    if (highlighted && isCritical) return "#EF4444"; // brighter red

    // Base semantic colors
    if (isInChain) return "#1A73E8"; // chain blue
    if (isCritical) return "#CC0000"; // critical red

    // Default
    return highlighted ? "#4285F4" : "#1b1b17";
  }, [highlighted, isInChain, isCritical]);

  const padding = 12;
  const iconSize = 18;
  const statusWidth = 70;
  const statusHeight = 28;
  const rowSpacing = (height - padding * 2) / 3;
  const titleY = padding + rowSpacing * 0.55;
  const subtitleY = padding + rowSpacing * 1.55;
  const rowY = padding + rowSpacing * 2.55;
  const assigneeList = Array.isArray(d)
    ? d.filter((n) => n && n.trim())
    : d && d.trim()
      ? [d]
      : [];
  const hasAssignee = assigneeList.length > 0;
  const assigneeColor = hasAssignee ? "#075985" : "#B0B0B0";
  const assigneeInitials = hasAssignee ? getInitials(assigneeList[0]) : "";
  const isMultipleAssignees = assigneeList.length > 1;

  const isTeamProj = id && (id.startsWith("uuid:") || id.includes("-"));

  const getMaturityDisplay = (m) => {
    if (!m) return "No Status";
    const s = String(m).toLowerCase().trim();
    if (isTeamProj) {
      if (s === "done" || s === "complete" || s === "completed") return "Completed";
      if (s === "open") return "To Do";
      if (s === "inprogress") return "In Work";
      return m;
    }

    if (s === "late") return "Late";
    if (s === "delay" || s === "delayed") return "Delayed";
    if (s === "active") return "In Work";
    if (s === "create") return "Draft";
    if (s === "assign") return "To Do";

    if (s.includes("to do") || s.includes("todo")) return "To Do";
    if (s.includes("in work") || s.includes("in-work") || s === "inwork")
      return "In Work";
    if (s.includes("draft")) return "Draft";
    if (s.includes("in progress") || s.includes("inprogress"))
      return "In Work";
    if (s.includes("review") || s.includes("in review")) return "In Review";
    if (s.includes("complete") || s.includes("completed") || s.includes("done"))
      return "Completed";
    if (s.includes("release") || s.includes("released")) return "Released";
    if (s.includes("late") || s.includes("delayed")) return "Delayed";

    // Capitalize first letter of each word as fallback
    return m
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getMaturityColor = (m) => {
    if (!m) return "rgb(110,110,110)"; // fallback muted grey

    const s = String(m).toLowerCase().trim();

    if (
      s === "active" ||
      s.includes("in work") ||
      s.includes("in-work") ||
      s.includes("inwork") ||
      s.includes("in progress") ||
      s === "inprogress"
    ) {
      return showWarningIcon ? "rgba(150, 59, 59, 1)" : "#FFFFFF";
    }
    if (s === "create" || s.includes("draft")) {
      return "rgb(120,120,120)"; // professional muted grey
    }

    if (s === "assign" || s.includes("to do") || s.includes("todo") || s === "open") {
      return "rgb(110,110,110)"; // muted deep blue
    }
    if (s.includes("review")) {
      return "rgb(70,65,125)"; // muted indigo
    }
    if (s.includes("complete") || s.includes("done")) {
      return "rgba(11, 139, 21, 1)"; // muted teal
    }
    if (s.includes("release")) {
      return "rgba(37, 165, 224, 1)"; // muted slate blue
    }

    if (s.includes("late") || s.includes("delay")) {
      return "rgba(150, 59, 59, 1)"; // muted red
    }

    return "rgb(110,110,110)";
  };

  const displayMaturity = getMaturityDisplay(maturity);
  const maturityColor = getMaturityColor(maturity);

  const subtitleIconMap = {
    "Pending Approval": (
      <HourglassEmptyIcon style={{ fontSize: iconSize, color: "#F28C38" }} />
    ),
    Phase: <AutorenewIcon style={{ fontSize: iconSize, color: "#234A96" }} />,
    Milestone: <FlagIcon style={{ fontSize: iconSize, color: "#2E7D32" }} />,
    Completed: (
      <CheckCircleIcon style={{ fontSize: iconSize, color: "#2E7D32" }} />
    ),
    Task: <AssignmentIcon style={{ fontSize: iconSize, color: "#707070" }} />,
    Late: <WarningIcon style={{ fontSize: iconSize, color: "#D9534F" }} />,
    Delayed: <WarningIcon style={{ fontSize: iconSize, color: "#D9534F" }} />,
  };

  const subtitleIcon = subtitleIconMap[subtitle] || (
    <HourglassEmptyIcon style={{ fontSize: iconSize, color: "#707070" }} />
  );



  const openMenu = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    hideLocalTooltip();

    // Use global function to open this dropdown
    openGlobalDropdown(id);
  }, [id]);

  const closeMenu = useCallback(() => {
    // Use global function to close this dropdown
    closeGlobalDropdown(id);
  }, [id]);

  // Effect to handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Also check if the click is not on the dropdown menu itself
        const dropdownMenu = document.querySelector('[data-dropdown-menu]');
        if (dropdownMenu && !dropdownMenu.contains(event.target)) {
          closeMenu();
        }
      }
    };

    if (isThisDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isThisDropdownOpen, closeMenu]);

  const isChainRoot = chainRootId === id;

  const isExitDisabled =
    isInChain && chainRootId && !isChainRoot && exitedChainNodes.has(id); // ✅ KEY FIX

  return (
    <g
      ref={nodeRef}
      transform={`translate(${x}, ${y})`}
      opacity={opacity}
      onMouseDown={(e) => onMouseDown && onMouseDown(e)}
      onMouseEnter={() => {
        setHighlighted(true);
        const displayAssignee = hasAssignee
          ? assigneeList.join(", ")
          : "No Assignee";
        const tooltipString =
          `Title: ${title}\n` +
          `Type: ${subtitle}\n` +
          `Estimated Start Date: ${startDate || "—"}\n` +
          `Due Date: ${dueDate || "—"}\n` +
          `Assignee: ${displayAssignee}\n` +
          `Maturity: ${displayMaturity || "—"}`; // UPDATED: Show maturity instead of status

        // React node for tooltip rendering with bold headers
        const tooltipNode = (
          <>
            <strong>Title:</strong> {title} <br />
            <strong>Type:</strong> {subtitle} <br />
            <strong>Estimated Start Date:</strong> {startDate || "—"} <br />
            <strong>Due Date:</strong> {dueDate || "—"} <br />
            <strong>
              {isMultipleAssignees ? "Assignees:" : "Assignee:"}
            </strong>{" "}
            {hasAssignee ? assigneeList.join(", ") : "No Assignee"} <br />
            <strong>Maturity:</strong> {displayMaturity || "—"} <br />{" "}
            {delayDays > 0 && (
              <>
                <strong style={{ color: "#D9534F" }}>Delayed By:</strong> {delayDays} Days <br />
              </>
            )}
            {/* UPDATED */}
            {maturityDescription && (
              <>
                <strong>Maturity Description:</strong> {maturityDescription}
                <br />
              </>
            )}
            {description && (
              <>
                <strong>Description:</strong> {description}
              </>
            )}
          </>
        );
        showLocalTooltip(tooltipString, tooltipNode);
      }}
      onMouseMove={() => {
        if (!tooltip.visible) {
          setHighlighted(true);
          const displayAssignee = hasAssignee
            ? assigneeList.join(", ")
            : "No Assignee";
          const tooltipString =
            `Title: ${title}\n` +
            `Type: ${subtitle}\n` +
            `Estimated Start Date: ${startDate || "—"}\n` +
            `Due Date: ${dueDate || "—"}\n` +
            `Assignee: ${displayAssignee}\n` +
            `Maturity: ${displayMaturity || "—"}`; // UPDATED: Show maturity instead of status

          const tooltipNode = (
            <>
              <strong>Title:</strong> {title} <br />
              <strong>Type:</strong> {subtitle} <br />
              <strong>Estimated Start Date:</strong> {startDate || "—"} <br />
              <strong>Due Date:</strong> {dueDate || "—"} <br />
              <strong>
                {isMultipleAssignees ? "Assignees:" : "Assignee:"}
              </strong>{" "}
              {hasAssignee ? assigneeList.join(", ") : "No Assignee"} <br />
              <strong>Maturity:</strong> {displayMaturity || "—"} <br />{" "}
              {delayDays > 0 && (
                <>
                  <strong style={{ color: "#D9534F" }}>Delayed By:</strong> {delayDays} Days <br />
                </>
              )}
              {/* UPDATED */}
              {maturityDescription && (
                <>
                  <strong>Maturity Description:</strong> {maturityDescription}
                  <br />
                </>
              )}
              {description && (
                <>
                  <strong>Description:</strong> {description}
                </>
              )}
            </>
          );

          showLocalTooltip(tooltipString, tooltipNode);
        }
      }}
      onMouseLeave={() => {
        setHighlighted(false);
        hideLocalTooltip();
      }}
      style={{
        cursor: dragNode === id ? "grabbing" : "grab",
        userSelect: "none",
      }}
    >
      <rect
        width={width}
        height={height}
        rx={6}
        ry={6}
        fill={nodeColor || "#FFFFFF"}
        stroke={rectStroke}
        strokeWidth={isInChain || isCritical ? 3 : 1}
        filter={
          isInChain && isCritical
            ? "url(#chainNodeElevation)" // no red glow by default
            : isInChain
              ? "url(#chainNodeElevation)"
              : isCritical && highlighted
                ? "url(#criticalNodeGlow)" // glow ONLY on hover
                : highlighted
                  ? "url(#nodeHover)"
                  : "none"
        }
        style={{
          transition:
            highlighted && !isInChain
              ? "filter 0.3s ease, stroke-width 0.3s ease"
              : "none",
        }}
      />

      {/* Loading Indicator Overlay */}
      {loading && (
        <foreignObject x={0} y={0} width={width} height={height}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "6px",
              pointerEvents: "none"
            }}
          >
            <div className="node-spinner"></div>
            <style>{`
              .node-spinner {
                width: 20px;
                height: 20px;
                border: 2px solid #234A96;
                border-top: 2px solid transparent;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </foreignObject>
      )}

      {/* Title — native SVG text */}
      <text
        x={padding}
        y={titleY - 2}
        fontSize="13"
        fontWeight="600"
        fill="#234A96"
        fontFamily="Arial, sans-serif"
      >
        {title.length > 25 ? title.substring(0, 22) + "..." : title}
      </text>

      {/* Dropdown */}
      <g
        ref={dropdownRef}
        transform={`translate(${width - 28}, 10)`}
        onClick={(e) => {
          e.stopPropagation();
          if (isThisDropdownOpen) closeMenu();
          else openMenu(e);
        }}
        style={{ cursor: "pointer" }}
      >
        <foreignObject width={20} height={20}>
          <ArrowDropDownIcon style={{ fontSize: 20, color: "#5A5A5A" }} />
        </foreignObject>
      </g>

      {/* Expand / Collapse Toggle */}
      {hasChildren && !timelineMode && (
        <g
          transform={`translate(${width - 9}, -6)`}
          onClick={(e) => {
            e.stopPropagation();
            if (onDoubleClick) onDoubleClick();
          }}
          style={{ cursor: "pointer" }}
        >
          <rect
            width="20"
            height="20"
            rx="4"
            ry="4"
            fill="#eee"
            stroke="#666"
            strokeWidth="1"
          />

          <foreignObject x="2" y="2" width="16" height="16">
            {isExpanded ? (
              <RemoveIcon
                style={{
                  fontSize: 16,
                  color: "#555",
                  width: "16px",
                  height: "16px",
                }}
              />
            ) : (
              <AddIcon
                style={{
                  fontSize: 16,
                  color: "#555",
                  width: "16px",
                  height: "16px",
                }}
              />
            )}
          </foreignObject>
        </g>
      )}

      {/* Dropdown Menu */}
      {isThisDropdownOpen &&
        dropdownRef.current &&
        createPortal(
          (() => {
            const rect = dropdownRef.current.getBoundingClientRect();
            const taskId = objectId || id;

            return (
              <div
                data-dropdown-menu // Added data attribute for click detection
                style={{
                  position: "fixed",
                  top: rect.top,
                  left: rect.right + 16,
                  width: 150,
                  background: "white",
                  border: "1px solid #C8C6B8",
                  borderRadius: "4px",
                  fontSize: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                  fontFamily: "Arial",
                  padding: "4px",
                  zIndex: 999999999,
                  pointerEvents: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: 8,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor:
                      hoverIndex === 0 ? "#bbb8b88a" : "transparent",
                    borderRadius: 4,
                    transition: "background 0.2s ease",
                    borderBottom: "1px solid #ccc",
                  }}
                  onMouseEnter={() => setHoverIndex(0)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      `${ENOVIA_BASE_URL}/common/emxNavigator.jsp?isPopup=false&objectId=${taskId}`,
                      "_blank"
                    );
                  }}
                >
                  <CheckCircleOutlineIcon
                    style={{ fontSize: 16, marginRight: 6 }}
                  />
                  Open
                </div>

                {/* Add Deliverables Option */}
                <div
                  style={{
                    padding: 8,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor:
                      hoverIndex === 10 ? "#bbb8b88a" : "transparent",
                    borderRadius: 4,
                    transition: "background 0.2s ease",
                    borderBottom: "1px solid #ccc",
                  }}
                  onMouseEnter={() => setHoverIndex(10)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenu();
                    if (onAddDeliverables) {
                      onAddDeliverables(taskId, title);
                    }
                  }}
                >
                  <AttachFileIcon style={{ fontSize: 16, marginRight: 6, color: "#1A73E8" }} />
                  Add Deliverables
                </div>

                {/* Chain Option */}

                {hasChildren && (
                  <div
                    style={{
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      cursor: (isExitDisabled || timelineMode) ? "not-allowed" : "pointer",
                      opacity: (isExitDisabled || timelineMode) ? 0.4 : 1,
                      backgroundColor:
                        hoverIndex === 3 && !(isExitDisabled || timelineMode)
                          ? "#bbb8b88a"
                          : "transparent",
                      borderRadius: 4,
                      borderBottom: "1px solid #ccc",
                      transition: "background 0.2s ease",
                      pointerEvents: (isExitDisabled || timelineMode) ? "none" : "auto",
                    }}
                    onMouseEnter={() => !(isExitDisabled || timelineMode) && setHoverIndex(3)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      if (timelineMode) return;
                      e.stopPropagation();
                      e.preventDefault();

                      if (isExitDisabled) return;

                      if (onHighlightChain) {
                        if (isInChain) {
                          onHighlightChain(id, true);
                        } else {
                          onHighlightChain(id, false);
                        }
                      }

                      closeMenu();
                    }}
                    title={timelineMode ? "Layered view is disabled in Timeline Mode" : ""}
                  >
                    <LayersIcon
                      style={{
                        fontSize: 16,
                        marginRight: 6,
                        color: isExitDisabled
                          ? "#999"
                          : isInChain
                            ? "#1A73E8"
                            : "#234A96",
                      }}
                    />
                    {isExitDisabled
                      ? "Show Layered View"
                      : isInChain
                        ? "Exit Layered View"
                        : "Show Layered View"}
                  </div>
                )}

                {(() => {
                  const isCompleted = displayMaturity === "Completed" || displayMaturity === "Released";
                  return (
                    <div
                      style={{
                        padding: 8,
                        display: "flex",
                        alignItems: "center",
                        cursor: isCompleted ? "not-allowed" : "pointer",
                        backgroundColor: hoverIndex === 1 && !isCompleted ? "#bbb8b88a" : "transparent",
                        borderRadius: 4,
                        transition: "background 0.2s ease",
                        borderBottom: "1px solid #ccc",
                        opacity: isCompleted ? 0.5 : 1,
                      }}
                      onMouseEnter={() => !isCompleted && setHoverIndex(1)}
                      onMouseLeave={() => setHoverIndex(null)}
                      onClick={(e) => {
                        if (isCompleted) return;
                        e.stopPropagation();
                        closeGlobalDropdown(id);
                        setShowAssigneePopup(true);
                      }}
                    >
                      <PersonIcon style={{ fontSize: 16, marginRight: 6 }} />
                      Change Assignee
                    </div>
                  );
                })()}

                {(() => {
                  const isPromoteDisabled = displayMaturity === "Completed" || displayMaturity === "Released" || subtitle === "Phase";
                  return (
                    <div
                      style={{
                        padding: 8,
                        display: "flex",
                        alignItems: "center",
                        cursor: isPromoteDisabled ? "not-allowed" : "pointer",
                        backgroundColor: hoverIndex === 2 && !isPromoteDisabled ? "#bbb8b88a" : "transparent",
                        borderRadius: 4,
                        transition: "background 0.2s ease",
                        opacity: isPromoteDisabled ? 0.5 : 1,
                      }}
                      onMouseEnter={() => !isPromoteDisabled && setHoverIndex(2)}
                      onMouseLeave={() => setHoverIndex(null)}
                      onClick={(e) => {
                        if (isPromoteDisabled) return;
                        e.stopPropagation();
                        const taskIdToPromote = objectId || id;
                        if (!taskIdToPromote) return;
                        // Calculate next state based on current maturity
                        const getNextState = (currentMaturity) => {
                          const s = String(currentMaturity || "").toLowerCase().trim();
                          if (isTeamProj) {
                            if (s === "draft") return "Open";
                            if (s === "open") return "InProgress";
                            if (s === "inprogress") return "Done";
                            return "Open";
                          }
                          if (s === "create" || s.includes("draft")) return "Assign";
                          if (s === "assign" || s.includes("to do") || s.includes("todo")) return "Active";
                          if (s === "active" || s.includes("in work") || s.includes("progress")) return "Review";
                          if (s.includes("review")) return "Complete";
                          return "Active"; // Fallback
                        };
                        const nextState = getNextState(maturity);

                        closeGlobalDropdown(id);
                        setHoverIndex(null);
                        setTooltip((t) => ({ ...t, visible: false }));

                        if (onPromoteNode) {
                          onPromoteNode(id, taskIdToPromote, nextState, maturity);
                        } else {
                          if (onUpdateNode) {
                            onUpdateNode(id, { maturity: nextState });
                          }
                          Service.EnoviaService()
                            .promoteTask(taskIdToPromote, nextState)
                            .then((res) => {
                              showSuccessToast("Task promoted successfully!");
                              if (!onUpdateNode && onRefresh) onRefresh();
                            })
                            .catch((err) => {
                              showErrorToast("Failed to promote task. Reverting state...");
                              if (onUpdateNode) {
                                onUpdateNode(id, { maturity: maturity });
                              }
                            });
                        }
                      }}
                    >
                      <ArrowUpwardIcon style={{ fontSize: 16, marginRight: 6 }} />
                      Promote
                    </div>
                  );
                })()}
              </div>
            );
          })(),
          document.body
        )}

      {/* Subtitle icon — kept as foreignObject (MUI icon component) */}
      <foreignObject
        x={padding}
        y={subtitleY - iconSize / 2 - 4}
        width={iconSize}
        height={iconSize}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {subtitleIcon}
        </div>
      </foreignObject>

      {/* Native SVG text for subtitle */}
      <text x={padding + iconSize + 6} y={subtitleY} fontSize="13" fill="#0f0e0eff" fontFamily="Arial, sans-serif">
        {subtitle}
      </text>
      <DeliverablesDots taskId={objectId || id} x={width - padding - 60} y={subtitleY - 2} setTooltip={setTooltip} zoomRatio={zoomRatio} />

      {/* Bottom Row */}
      <g transform={`translate(${padding}, ${rowY})`}>
        <text x={0} y={-6} fontSize="8" fill="#999" fontWeight="600" letterSpacing="0.5" fontFamily="Arial, sans-serif">DUE DATE</text>
        <text x={0} y={7} fontSize="13" fill="#333" fontFamily="Arial, sans-serif">
          {dueDate}
        </text>

        {/* Assignee circle */}
        <g
          transform={`translate(${width - padding * 2 - statusWidth - 10 - 18},${iconSize / 4 - 6})`}
          onMouseEnter={() => {
            const displayAssignee =
              Array.isArray(d) && d.length > 0
                ? d.join(", ")
                : d && typeof d === "string" && d.trim()
                  ? d
                  : "No Assignee";
            const tooltipNode = (
              <>
                <strong>Assignee:</strong> {displayAssignee}
              </>
            );
            showLocalTooltip(`Assignee: ${displayAssignee}`, tooltipNode);
          }}
          onMouseLeave={hideLocalTooltip}
          style={{ cursor: "default" }}
        >
          {isMultipleAssignees ? (
            <foreignObject
              x={-iconSize / 2 - 4}
              y={-iconSize / 2 - 6}
              width={iconSize + 8}
              height={iconSize + 8}
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  width: iconSize + 8,
                  height: iconSize + 8,
                  borderRadius: "50%",
                  backgroundColor: "#075985",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <GroupIcon style={{ fontSize: iconSize, color: "#fff" }} />
              </div>
            </foreignObject>
          ) : (
            <>
              <circle r={iconSize / 2 + 4} fill={assigneeColor} />
              {assigneeInitials && (
                <text x="0" y="4" fontSize="13" textAnchor="middle" fill="white" fontWeight="600" fontFamily="Arial, sans-serif">
                  {assigneeInitials}
                </text>
              )}
            </>
          )}
        </g>

        {/* Status pill - Now showing Maturity */}
        <g
          transform={`translate(${width - padding * 2 - statusWidth}, ${-iconSize / 2 - 8})`}
          onMouseEnter={() => {
            const tooltipString = `Maturity: ${displayMaturity || "—"}\n${maturityDescription || ""
              }`;
            const tooltipNode = (
              <>
                <strong>Maturity:</strong> {displayMaturity || "—"} <br />
                {maturityDescription && (
                  <>
                    <strong>Maturity Description:</strong> {maturityDescription}
                    <br />
                  </>
                )}
              </>
            );
            showLocalTooltip(tooltipString, tooltipNode);
          }}
          onMouseLeave={hideLocalTooltip}
        >
          <rect
            width={statusWidth}
            height={statusHeight}
            rx={10}
            fill={maturityColor}
            stroke={maturityColor === "#FFFFFF" ? "#C8C6B8" : "none"}
            strokeWidth={1}
          />
          {/* Native SVG text centered inside the pill */}
          <text
            x={statusWidth / 2}
            y={statusHeight / 2 + 1}
            fontSize="13"
            fontWeight="600"
            fill={maturityColor === "#FFFFFF" ? "#333333" : "white"}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Arial, sans-serif"
          >
            {displayMaturity}
          </text>
        </g>
      </g>

      {/* Tooltip */}
      {!isThisDropdownOpen && !showAssigneePopup &&
        tooltip.visible &&
        createPortal(
          (() => {
            const nodeRect = nodeRef.current?.getBoundingClientRect() || {
              top: 0, left: 0, bottom: 0, right: 0,
            };
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const MARGIN = 8;
            // Flip above if too close to bottom
            const spaceBelow = vh - nodeRect.bottom;
            const tooltipTop = spaceBelow < tooltip.height + MARGIN
              ? Math.max(MARGIN, nodeRect.top - tooltip.height - 4)
              : nodeRect.bottom + 4;
            // Clamp left so tooltip stays within viewport
            const rawLeft = nodeRect.right - tooltip.width;
            const tooltipLeft = Math.max(MARGIN, Math.min(rawLeft, vw - tooltip.width - MARGIN));
            return (
              <div
                style={{
                  position: "fixed",
                  top: tooltipTop,
                  left: tooltipLeft,
                  minWidth: tooltip.width,
                  background: "white",
                  border: "1px solid #363a3fff",
                  borderRadius: "8px",
                  padding: "8px",
                  fontSize: "12px",
                  color: "#202124",
                  whiteSpace: "pre-line",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  pointerEvents: "none",
                  zIndex: 999999999,
                  lineHeight: "1.6",
                }}
              >
                {tooltip.textNode}
              </div>
            );
          })(),
          document.body
        )}
      {showAssigneePopup && (
        <AssigneePopup
          taskId={objectId || id}
          projectId={projectId}
          taskName={title}
          onClose={() => setShowAssigneePopup(false)}
          onUpdate={(newAssignees) => {
            if (onUpdateNode) {
              onUpdateNode(id, { assignees: newAssignees });
            }
          }}
        />
      )}
    </g>
  );
};

export default RectangleNode;